// Phase 2.2 Lite — Client Review URLs panel.
// Mounts inside Event Setup → Digital Invitation tab. Lets the designer:
//   • Generate a secure, token-protected /review/{slug}?token=... link
//   • Copy the link
//   • Watch the reviewer's status (Sent / Approved / Changes requested)
//   • Read the latest reviewer note (when changes_requested)
//   • Regenerate the token (old link dies)
//   • Disable the review link entirely
//
// Auto-polls /events/{eventId}/review-link every 30s while mounted/visible
// so the designer sees status updates without refreshing.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Loader2, Link as LinkIcon, Copy, RotateCcw, Eye, EyeOff, Check, MessageSquare, ArrowUpRight,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 30_000;

const STATUS_STYLES = {
  draft:             { label: 'Draft',             tone: 'bg-gray-200 text-gray-700' },
  sent_for_review:   { label: 'Sent for review',   tone: 'bg-sky-100 text-sky-800' },
  approved:          { label: 'Approved',          tone: 'bg-emerald-100 text-emerald-800' },
  changes_requested: { label: 'Changes requested', tone: 'bg-amber-100 text-amber-800' },
};

function formatRelative(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const diff = Date.now() - t;
  const min = Math.round(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.round(hr / 24);
  return `${d}d ago`;
}

function reviewUrlFor(meta) {
  if (!meta?.review_url) return null;
  // backend returns a path like "/review/{slug}?token=..."
  return `${window.location.origin}${meta.review_url}`;
}

export default function ReviewLinkPanel({ eventId, adminDarkMode = false }) {
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // 'enable' | 'disable' | 'regenerate'
  const pollRef = useRef(null);

  const fetchMeta = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await axios.get(
        `${API}/api/events/${eventId}/review-link`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
      );
      setMeta(r.data);
    } catch {
      if (!silent) toast.error('Could not load review link');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [eventId]);

  // Initial load + 30s poll while visible.
  useEffect(() => {
    fetchMeta();
    const start = () => {
      stop();
      pollRef.current = setInterval(() => {
        if (document.visibilityState === 'visible') fetchMeta(true);
      }, POLL_MS);
    };
    const stop = () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
    start();
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        fetchMeta(true);
        start();
      } else {
        stop();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [fetchMeta]);

  const callAction = async (verb) => {
    setBusy(verb);
    try {
      const r = await axios.post(
        `${API}/api/events/${eventId}/review-link/${verb}`,
        verb === 'enable' ? {} : undefined,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
      );
      if (verb === 'enable') toast.success('Review link is live');
      if (verb === 'disable') toast.success('Review link disabled');
      if (verb === 'regenerate') toast.success('New review link generated — old link is now invalid');
      // Re-fetch so we get fresh metadata (and the review_url for copy).
      await fetchMeta(true);
      return r;
    } catch (err) {
      toast.error(err.response?.data?.detail || `Could not ${verb}`);
    } finally {
      setBusy(null);
    }
  };

  const handleCopy = async () => {
    const url = reviewUrlFor(meta);
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Review link copied');
    } catch {
      toast.error('Copy failed — select and copy manually');
    }
  };

  const enabled = !!meta?.review_enabled;
  const status = meta?.review_status || 'draft';
  const styleConfig = STATUS_STYLES[status] || STATUS_STYLES.draft;
  const url = reviewUrlFor(meta);
  const sentRel = formatRelative(meta?.review_sent_at);
  const approvedRel = formatRelative(meta?.review_approved_at);
  const changesRel = formatRelative(meta?.review_changes_requested_at);

  const panelTone = adminDarkMode
    ? 'border-gray-700 bg-gray-800/50'
    : 'border-gray-200 bg-white';
  const headingTone = adminDarkMode ? 'text-gray-200' : 'text-gray-800';
  const muted = adminDarkMode ? 'text-gray-400' : 'text-gray-500';

  return (
    <div
      className={`p-5 rounded-xl border ${panelTone}`}
      data-testid="review-link-panel"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <h3 className={`text-sm font-semibold flex items-center gap-2 ${headingTone}`}>
            <Eye className="w-4 h-4" />
            Send for client review
          </h3>
          <p className={`text-xs mt-1 ${muted}`}>
            Share a private, token-gated preview link. Your client can approve or request changes — without touching the live invitation.
          </p>
        </div>
        <span
          className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-medium ${styleConfig.tone}`}
          data-testid="review-link-status-badge"
        >
          {styleConfig.label}
        </span>
      </div>

      {loading ? (
        <div className={`flex items-center gap-2 text-xs ${muted}`}>
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
        </div>
      ) : !enabled ? (
        <div className="space-y-3">
          <p className={`text-xs ${muted}`}>No review link yet.</p>
          <button
            onClick={() => callAction('enable')}
            disabled={busy === 'enable'}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
            data-testid="review-link-enable-btn"
          >
            {busy === 'enable' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LinkIcon className="w-3.5 h-3.5" />}
            Generate review link
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {/* URL row */}
          <div
            className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${adminDarkMode ? 'border-gray-700 bg-gray-900/40' : 'border-gray-200 bg-gray-50'}`}
          >
            <LinkIcon className={`w-3.5 h-3.5 shrink-0 ${muted}`} />
            <code
              className={`text-[11px] truncate flex-1 ${adminDarkMode ? 'text-gray-300' : 'text-gray-700'}`}
              data-testid="review-link-url"
            >
              {url}
            </code>
            <button
              onClick={handleCopy}
              className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium bg-white text-gray-700 border border-gray-200 hover:bg-gray-100"
              data-testid="review-link-copy-btn"
            >
              <Copy className="w-3 h-3" />
              Copy
            </button>
          </div>

          {/* Activity timeline (compact) */}
          <ul className={`text-[11px] space-y-1 ${muted}`} data-testid="review-link-activity">
            {sentRel && (
              <li className="flex items-center gap-1.5">
                <LinkIcon className="w-3 h-3 opacity-60" />
                Sent {sentRel}
              </li>
            )}
            {approvedRel && (
              <li className="flex items-center gap-1.5 text-emerald-600">
                <Check className="w-3 h-3" />
                Approved {approvedRel}
              </li>
            )}
            {changesRel && (
              <li className="flex items-center gap-1.5 text-amber-600">
                <MessageSquare className="w-3 h-3" />
                Changes requested {changesRel}
              </li>
            )}
          </ul>

          {/* Note from reviewer (only meaningful when changes_requested) */}
          {status === 'changes_requested' && meta?.review_note && (
            <div
              className={`p-3 rounded-lg text-xs border ${adminDarkMode ? 'border-amber-500/30 bg-amber-900/10 text-amber-200' : 'border-amber-200 bg-amber-50 text-amber-900'}`}
              data-testid="review-link-note"
            >
              <div className="flex items-start gap-2">
                <MessageSquare className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="whitespace-pre-wrap leading-relaxed">{meta.review_note}</p>
                  <button
                    onClick={() => navigate(`/events/${eventId}/invitation-editor`)}
                    className={`mt-2 inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium ${adminDarkMode ? 'bg-amber-500/20 hover:bg-amber-500/30 text-amber-100' : 'bg-amber-100 hover:bg-amber-200 text-amber-900'}`}
                    data-testid="review-link-view-in-studio-btn"
                  >
                    View in Studio
                    <ArrowUpRight className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Action row */}
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <button
              onClick={() => callAction('regenerate')}
              disabled={busy === 'regenerate'}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-medium bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              data-testid="review-link-regenerate-btn"
            >
              {busy === 'regenerate' ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
              Regenerate link
            </button>
            <button
              onClick={() => callAction('disable')}
              disabled={busy === 'disable'}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-medium bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              data-testid="review-link-disable-btn"
            >
              {busy === 'disable' ? <Loader2 className="w-3 h-3 animate-spin" /> : <EyeOff className="w-3 h-3" />}
              Disable
            </button>
            <span className={`text-[10px] ml-auto ${muted}`}>
              Auto-checks every 30s
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
